import CloseIcon from '@mui/icons-material/Close';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, FormHelperText, Grid, IconButton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/system';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '../components/TimePicker';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt } from 'react-router';

import { DataAPIContext, UPDATE } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RevisionHistoryDialog from '../components/RevisionHistoryDialog';
import TabPanel, { a11yProps } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard } from '../utils/clipboard';
import AssociatedUserField from './AssociatedUserField';
import { BindingChips, bindingsByField, changedBoundTargets, tagSaveBody } from './bindings';
import SchemaSections from './SchemaSections';
import { ConfirmationDialogText, identityFieldMode } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        marginTop: '1rem',
    },
    gridRoot: {
        overflowY: 'scroll',
        overflowX: 'hidden',
        height: 'calc(100svh - 17rem)',
        padding: theme.spacing(2),
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100svh - 20rem)',
            padding: theme.spacing(1),
        },
    },
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
    textFieldHelperText: {
        textTransform: 'none',
    },
}));

const validateInput = (tag) => {
    let canSave = true;
    if (!tag.name || tag.name === '') {
        canSave = false;
    }

    return canSave
};

const getComparableState = (tag) => {
    const a = {
        ...tag,
    };
    delete a._changed;
    return JSON.stringify(a);
};

const TagEdit = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'tour_tag';
    // const history = useHistory();
    const classes = useStyles();
    const [syncActive, setSyncActive] = React.useState(false);
    const { hasPerm } = React.useContext(ContextStore);
    const theme = useTheme();

    // eslint-disable-next-line no-unused-vars
    const [serverErrors, setServerErrors] = React.useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [tag, setTag] = React.useState({ ...props.data, data: props.data.data || {}, _changed: false });

    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [missingNameError, setMissingNameError] = React.useState(false);
    const [currentTab, setCurrentTab] = React.useState(0);

    const [requiredTagFieldsError, setRequiredTagFieldsError] = React.useState(false);
    const [missingRequired, setMissingRequired] = React.useState([]);
    // Translation bindings (metaplan A6/A10): the data as last loaded or saved, to tell which bound
    // (auto-translated) leaves this edit changes; and the open "keep or unbind?" question.
    const [savedData, setSavedData] = React.useState(props.data.data || {});
    const [unbindPrompt, setUnbindPrompt] = React.useState(null);
    const bindingIndex = bindingsByField(tag.translation_bindings);
    const [existingTagError, setExistingTagError] = React.useState(false);
    const hasWritePerm = hasPerm('tag', 'write')

    const onLicenseeChange = (id) => {
        setTag((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    if (tag.id !== props.data.id) {
        setTag({ ...props.data, data: props.data.data || {} });
        setSavedData(props.data.data || {});
        setUnbindPrompt(null);
    }

    const originalState = getComparableState(props.data);
    const currentState = getComparableState(tag);
    const isChanged = originalState !== currentState;

    const handleChange = (ev) => {
        if (ev.target.name === 'name' && existingTagError) setExistingTagError(false);
        setTag((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const handleDataChange = (ev) => {
        setTag((prevState) => ({ ...prevState, data: { ...prevState.data, [ev.target.name]: ev.target.value }, _changed: true }));
        // props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const onAssociatedUserChange = (ev, nv) => {
        setTag((prevState) => ({ ...prevState, user: nv, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const onAccessTagsChange = (ev, atags, reason) => {
        setTag((prevState) => ({ ...prevState, atag: atags, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const handleSave = (ev) => {
        console.log('save', tag);
        if (!checkIfRequiredAreMissing(tag)) {
            // AIE §8 "Unbind on direct edit": an edit of an auto-translated leaf asks first.
            const bound = changedBoundTargets(tag.translation_bindings, savedData, tag.data);
            if (bound.length > 0) {
                setUnbindPrompt(bound);
                return;
            }
            doSave([]);
        }
    };

    const doSave = (unbindIds) => {
        setSyncActive(true);
        (async () => {
            try {
                const res = await dataAPI(UPDATE, resource, { id: tag.id, data: tagSaveBody(tag, unbindIds) });
                console.log('update', res);
                if (res.statusCode === 200) {
                    enqueueSnackbar(unbindIds.length ? 'Tag updated; auto-translation removed' : 'Tag updated', { variant: 'success' });
                    props.onChange();
                    setTag({ ...res.data, data: res.data.data || {} });
                    setSavedData(res.data.data || {});
                } else if (res.statusCode === 406) {
                    const message = res.data?.message || 'Tag name must be unique';
                    // Only flag the name field for the duplicate-name error; other
                    // 406s (unknown category / associated user) carry their own message.
                    setExistingTagError(/duplicate|unique/i.test(message));
                    enqueueSnackbar(message, { variant: 'error' });
                    console.log('validation errors', res.data);
                    setServerErrors(res.data?.errors);
                } else {
                    enqueueSnackbar('Error updating tag, please try again', { variant: 'error' });
                }
            } finally {
                setSyncActive(false);
            }
        })();
    };

    const doDelete = (ev) => {
        console.log('deleting', props.data.id);
        props.onDelete && props.onDelete(props.data);
        //     setSyncActive(true);
        //     (async () => {
        //         try {
        //             const res = await dataAPI(DELETE, props.resource, { id: props.data.id });
        //             enqueueSnackbar("Pre-trip item deleted", { variant: 'default' });
        //             console.log('delete', res);
        //             history.push(`/pretrip/items`, { deleted: props.data.id })
        //         } catch (e) {
        //             console.log('error deleting pre-trip item', e);
        //             enqueueSnackbar("Error deleting pre-trip item", { variant: 'error' });
        //         } finally {
        //             setSyncActive(false);
        //         }
        //     })();
    };

    const handleDelete = (ev) => {
        setConfirmationDialogAction('delete_tag');
    };

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
            //dir: theme.direction,
        };
    };

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    }

    const canSave = validateInput(tag) && hasPerm('tag', 'write');

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'delete_tag':
            case 'delete_tags':
                doDelete();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };
    const schema = props.data?.category?.schema ? props.data?.category?.schema : {};
    schema.properties = schema.properties ? schema.properties : {};

    const checkIfRequiredAreMissing = (tag) => {
        setRequiredTagFieldsError(false);
        let error = false;
        let required_fields = tag.category.schema?.required || [];
        let existing_fields = tag.data;
        const missing = [];
        for (let i = 0; i < required_fields.length; i++) {
            if (!(required_fields[i] in existing_fields)) {
                error = true;
                missing.push(required_fields[i]);
                setRequiredTagFieldsError(true);
            }
        }
        for (let i = 0; i < required_fields.length; i++) {
            if (required_fields[i] in existing_fields) {
                if (existing_fields[required_fields[i]] === '') {
                    error = true;
                    missing.push(required_fields[i]);
                    setRequiredTagFieldsError(true);
                }
            }
        }
        // A collapsed section opens again when it holds one of these (CP-7988).
        setMissingRequired(missing);
        return error;
    };

    const onBlur = (ev) => {
        setMissingNameError(false);
        if (ev.target.name === 'name') {
            if (!ev.target.value || ev.target.value?.replaceAll(' ', '') === '' || ev.target.value.length === 0) {
                setMissingNameError(true);
            }
        }
    };

    const onCheckBoxChange = (ev) => {
        setTag((prev) => ({ ...prev, data: { ...prev.data, [ev.target.name]: ev.target.checked }, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const onTimeChange = (time, key) => {
        setTag((prev) => ({ ...prev, data: { ...prev.data, [key]: time.format('HH:mm') }, _changed: true }));
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    const renderSchemaField = (key) => {
        const field = tag?.category?.schema?.properties[key];
        if (!field || typeof field !== 'object') return null;
        const type = field?.type;
        const format = field?.format;
        const multiline = !!field?.multiline;
        if (type === 'boolean') {
            return (
                <Grid item xs={12} key={key}>
                    <FormControlLabel
                        disabled={!hasWritePerm}
                        control={<Checkbox checked={tag?.data[key] || false} required={tag?.category?.schema?.required?.constructor === Array && tag?.category?.schema.required.indexOf(key) >= 0} onChange={onCheckBoxChange} name={key} color="primary" />}
                        label={tag?.category?.schema?.required?.constructor === Array && tag?.category?.schema.required.indexOf(key) >= 0 ? (field?.name || key) + ' *' : (field?.name || key)}
                    />
                    {field?.description && (
                        <FormHelperText>{field.description}</FormHelperText>
                    )}
                    <BindingChips field={key} index={bindingIndex} />
                </Grid>
            );
        }
        if (format === 'date-time') {
            return (
                <Grid item xs={12} key={key}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <TimePicker
                            disabled={!hasWritePerm}
                            label={field?.name || key}
                            value={tag?.data[key] ? moment(tag.data[key], 'HH:mm') : moment()}
                            onChange={(newValue) => {
                                onTimeChange(newValue, key);
                            }}
                            required={tag?.category?.schema?.required?.constructor === Array && tag?.category?.schema.required.indexOf(key) >= 0}
                            renderInput={(params) => <TextField fullWidth variant="filled" required={tag?.category?.schema?.required?.constructor === Array && tag?.category?.schema.required.indexOf(key) >= 0} helperText={field?.description} {...params} />}
                        />
                    </LocalizationProvider>
                    <BindingChips field={key} index={bindingIndex} />
                </Grid>
            );
        }
        return (
            <Grid item xs={12} key={key}>
                <TextField
                    disabled={!hasWritePerm}
                    variant="filled"
                    size="small"
                    fullWidth
                    required={tag?.category?.schema?.required?.constructor === Array && tag?.category?.schema.required.indexOf(key) >= 0}
                    name={key}
                    label={field?.title || field?.name || key}
                    value={tag?.data[key] ?? ''}
                    onChange={handleDataChange}
                    helperText={field?.description}
                    multiline={multiline}
                />
                <BindingChips field={key} index={bindingIndex} />
            </Grid>
        );
    };


    if (!hasPerm('tag', 'read')) {
        return <AccessDeniedView />
    }

    // console.log('tagedit', schema);
    return (
        <FormLicenseeContext.Provider value={toLicenseeId(tag.tour_operator)}>
        <div className={classes.root}>
            <PageMeta title={`Edit Tag - ${tag.name}`} />
            <Prompt when={!!tag._changed && hasPerm('tag', 'write')} message="Are you sure you want to leave without saving changes?" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                </ConfirmationDialog>
            ) : null}
            <Dialog open={!!unbindPrompt} onClose={() => setUnbindPrompt(null)} sx={{ zIndex: 10001 }}>
                <DialogTitle>Keep the auto-translation or unbind?</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        You changed {unbindPrompt?.length === 1 ? 'a value that is' : 'values that are'} translated
                        automatically:
                        <ul>
                            {(unbindPrompt || []).map((b) => (
                                <li key={b.id}>{`${b.target_label} (${b.target_lang}) from ${b.origin_label} (${b.origin_lang})`}</li>
                            ))}
                        </ul>
                        Unbind keeps your wording for good. Keeping the auto-translation saves your edit now, but it is
                        replaced the next time the original text changes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnbindPrompt(null)}>Cancel</Button>
                    <Button
                        onClick={() => {
                            setUnbindPrompt(null);
                            doSave([]);
                        }}
                    >
                        Keep the auto-translation
                    </Button>
                    <Button
                        variant="contained"
                        autoFocus
                        onClick={() => {
                            const ids = (unbindPrompt || []).map((b) => b.id);
                            setUnbindPrompt(null);
                            doSave(ids);
                        }}
                    >
                        Unbind and save
                    </Button>
                </DialogActions>
            </Dialog>
            <FixedLinearProgress visibility={syncActive} />
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12} container direction="row" alignItems="center" alignContent="center" >
                    <Grid item xs={8} p={2} pb={0}>
                        <Typography variant="h6">Tag Details</Typography>
                    </Grid>
                    <Grid item xs={4} container justifyContent="flex-end" alignItems="center">
                        <Button aria-label="close-preview" onClick={props.onClose}>
                            <CloseIcon />
                        </Button>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        <Tab label="General" {...a11yProps(0)} />
                        {hasPerm('tour_participant', 'view_history') && <Tab label="Change history" {...a11yProps(1)} />}
                        {tag.id && <Tab label="Used by" {...a11yProps(2)} />}
                    </Tabs>
                </Grid>
                <div className={classes.gridRoot}>

                    <TabPanel {...tabPanelProps(0)} style={{ margin: theme.spacing(2) }}>
                        <Grid item xs={12} container direction="row" spacing={2}>
                            <Grid item xs={12}>
                                <LicenseeFormField value={tag.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body1">Category: {tag.category.name}</Typography>
                                    {tag.category.external_identity && (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color="info"
                                            label="Externally identified"
                                            title="Tags in this category are identified by their External ID, not by name — so two of them may share a name."
                                        />
                                    )}
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField disabled={!hasWritePerm} required variant="filled" size="small" fullWidth name="name" label="Tag Name" value={tag.name} onChange={handleChange} onBlur={onBlur} error={missingNameError || existingTagError} helperText={existingTagError ? "Tag already exists" : (missingNameError && 'Tag name is required')} />
                            </Grid>
                            {/* On a name-identified category an External ID is not merely
                                empty -- the API refuses one -- so the field is absent there
                                rather than disabled. A tag that still carries one after its
                                category reverted is shown anyway, not silently dropped. */}
                            {identityFieldMode(tag) === 'field' ? (
                                <Grid item xs={12}>
                                    <TextField
                                        disabled={!hasWritePerm}
                                        variant="filled"
                                        color="primary"
                                        size="small"
                                        title="External ID"
                                        name="custom_id"
                                        label="External ID"
                                        value={tag.custom_id ?? ''}
                                        placeholder="Set external ID"
                                        onChange={handleChange}
                                        fullWidth
                                        inputProps={{ maxLength: 128 }}
                                        helperText="The upstream entity this tag mirrors. Changing it re-points the tag at a different entity; clearing it identifies this tag by name instead."
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    aria-label="copy tag external id to clipboard"
                                                    onClick={() => { copyToClipboard(tag.custom_id, 'Tag external ID'); }}
                                                    edge="end"
                                                >
                                                    <FileCopyIcon />
                                                </IconButton>
                                            ),
                                        }}
                                    />
                                </Grid>
                            ) : identityFieldMode(tag) === 'stranded' ? (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="warning.main">
                                        External ID {tag.custom_id} is set, but this category identifies tags by name.
                                    </Typography>
                                </Grid>
                            ) : null}
                            <Grid item xs={12}>
                                <AccessTagsDropdown disabled={!hasWritePerm} value={tag.atag} onChange={onAccessTagsChange} />
                            </Grid>
                            {/* Bindings on leaves the category schema does not list (e.g. nested or legacy keys). */}
                            {[...new Set([...Object.keys(bindingIndex.targets), ...Object.keys(bindingIndex.origins)])]
                                .filter((f) => !tag?.category?.schema?.properties?.[f])
                                .map((f) => (
                                    <Grid item xs={12} key={`binding-${f}`}>
                                        <Typography variant="caption" color="text.secondary">{f}</Typography>
                                        <BindingChips field={f} index={bindingIndex} />
                                    </Grid>
                                ))}
                            <SchemaSections
                                schema={tag?.category?.schema}
                                stateKey={tag?.category?.id}
                                attentionKeys={missingRequired}
                                renderField={renderSchemaField}
                            />
                            <Grid item xs={12}>
                                <AssociatedUserField disabled={!hasWritePerm} onChange={onAssociatedUserChange} value={tag.user} />
                            </Grid>
                            {requiredTagFieldsError && (
                                <Grid item xs={12} style={{ marginTop: '1rem' }}>
                                    <Typography color="secondary">Please fill in all required fields before saving</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </TabPanel>
                    <TabPanel {...tabPanelProps(1)} style={{ width: '100%', overflow: 'hidden' }}>
                        <RevisionHistoryDialog open={currentTab === 1} resource={resource} id={tag.id} />
                    </TabPanel>
                    {tag.id && <TabPanel {...tabPanelProps(2)} style={{ margin: theme.spacing(2) }}>
                        <UsedByPanel resource={resource} id={tag.id} layout="narrow" />
                    </TabPanel>}
                </div>

            </Grid>
            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    mode="edit"
                    onSubmit={handleSave}
                    submitDisabled={!canSave || !isChanged || !hasWritePerm}
                    onDelete={handleDelete}
                    deletePerm="tag.delete"
                    onCancel={props.onClose}
                />
            </Box>
        </div>
        </FormLicenseeContext.Provider>
    );
};

export default TagEdit;
